import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as SubframeCore from "@subframe/core";
import { Badge, Button, TextField, DropdownMenu } from "../ui";
import { Tooltip } from "../ui/components/Tooltip";
import {
  FeatherCheck,
  FeatherChevronDown,
  FeatherInfo,
  FeatherSearch,
  FeatherLoader,
  FeatherX,
} from "@subframe/core";
import { CustomerInvoice, CustomerListItem } from "../types";
import { TDSRate } from "../services/api";
import {
  useToast,
  SearchableDropdown,
} from "../shared/presentation/components";
import { useDropdownCache } from "../shared/presentation/hooks";
import { getAllCustomers, getCustomerById } from "../services/customerApi";
import { GetInvoicesForCustomerUseCase } from "../shared/domain/use-cases/GetInvoicesForCustomerUseCase";
import { InvoiceRepositoryImpl } from "../shared/data/repositories/InvoiceRepositoryImpl";
import { InvoiceListItemEntity } from "../shared/domain/entities/InvoiceListItemEntity";
import { isDocumentFxMismatch } from "../shared/utils/manualDocumentCurrency";
import { filterTdsRatesForDocumentDate } from "../shared/utils/tds";

// Permissions
import { usePermissions } from "../shared/presentation/hooks/usePermissions";
import { MODULE_CODES, ACTION_CODES } from "../shared/constants/permissions";

/**
 * ============================================================================
 * PERMISSION CHECKS DOCUMENTATION
 * ============================================================================
 *
 * Screen: Customer Payment Reconciliation Component
 * File: frontend/src/components/CustomerPaymentReco.tsx
 *
 * API Endpoints Used:
 * 1. GET /api/permissions/me
 *    - Purpose: Fetch all user permissions
 *    - Called: Once on component mount via usePermissions hook
 *    - Cached: Yes (in usePermissions hook)
 *
 * Request Body: N/A (GET request with JWT token in Authorization header)
 *
 * Response Structure:
 * {
 *   "success": true,
 *   "data": {
 *     "permissions": {
 *       "bank_transaction": {
 *         "advance": { "granted": true, "requires_review": false }
 *       }
 *     }
 *   },
 *   "message": "Permissions retrieved successfully"
 * }
 *
 * Interface Used:
 * - PermissionsMap from frontend/src/features/admin/domain/entities/PermissionEntity.ts
 *
 * Permission Checks Implemented:
 * 1. bank_transaction.advance
 *    - Checked via: getPermissionInfo(MODULE_CODES.BANK_TRANSACTION, ACTION_CODES.ADVANCE)
 *    - Used for: Advance payment input field enable/disable
 *    - Location: Advance payment TextField
 *    - Note: This component is used by CustomerPaymentTransactionModal which handles the review workflow
 *
 * Review Workflow: Handled by parent CustomerPaymentTransactionModal
 * ============================================================================
 */

interface SelectedInvoice {
  invoice: CustomerInvoice;
  amount: number;
  adjustmentAmount?: number;
  tds_tax_amount: number | undefined;
  tds_tax_id: string | undefined;
  tds_section: string | undefined;
  tds_tax_percentage: number | undefined;
  tds_tax_name: string | undefined;
  is_tds_applied: boolean | undefined | number;
  isSelected: boolean;
  lineItemsLoaded?: boolean;
  lineItemsLoading?: boolean;
  tds_line_items?: TdsLineItemSelection[];
}

interface TdsLineItemSelection {
  line_item_id: string;
  line_index: number;
  name: string;
  description: string;
  item_total: number;
  quantity: number;
  rate: number;
  tax_name?: string;
  tax_percentage?: number;
  tds_tax_id?: string;
  tds_tax_name?: string;
  tds_tax_percentage?: number;
  tds_section?: string;
  tds_tax_amount: number;
  is_tds_applied?: boolean;
}

interface CustomerPaymentRecoProps {
  id: string;
  transactionAmount: number;
  currencySymbol: string;
  tdsRates: TDSRate[];
  bankExchangeRate: number | null;
  onBankExchangeRateChange: (rate: number | null) => void;
  showRemoveButton?: boolean;
  onRemove?: () => void;
  isMultipleInstances?: boolean;
  totalAllocatedAmountsFromOtherInstances?: number;
  excludedCustomerIds?: string[];
  knockOffOpeningBalance?: boolean;
  onChange: (data: {
    customerId: string | null;
    customerName: string | null;
    selectedInvoices: SelectedInvoice[];
    advancePayment: number;
    onAccountAmount: number;
    totalInvoiceAmounts: number;
    bankExchangeRate: number | null;
  }) => void;
  autoSelectCustomerId?: string | null;
  autoSelectCustomerName?: string | null;
  onInitialPartyLoaded?: () => void;
  recommendationSource?: string | null;
  recommendationConfidence?: string | number | null;
}

const CustomerPaymentReco = React.forwardRef<
  HTMLDivElement,
  CustomerPaymentRecoProps
>(
  (
    {
      id,
      transactionAmount,
      currencySymbol,
      tdsRates,
      bankExchangeRate,
      onBankExchangeRateChange,
      showRemoveButton = false,
      onRemove,
      isMultipleInstances = false,
      totalAllocatedAmountsFromOtherInstances = 0,
      excludedCustomerIds = [],
      knockOffOpeningBalance = false,
      onChange,
      autoSelectCustomerId,
      autoSelectCustomerName,
      onInitialPartyLoaded,
      recommendationSource,
      recommendationConfidence,
    },
    ref,
  ) => {
    const { showWarning } = useToast();
    const { getCached, setCached } = useDropdownCache();

    // Permissions
    const { getPermissionInfo } = usePermissions();
    const advanceInfo = getPermissionInfo(
      MODULE_CODES.BANK_TRANSACTION,
      ACTION_CODES.ADVANCE,
    );
    const canAdvance = advanceInfo.granted;

    // Customer selection state
    const [selectedCustomer, setSelectedCustomer] =
      useState<CustomerListItem | null>(null);
    const [loadingZohoParty, setLoadingZohoParty] = useState(false);
    const [customerFromSuggestion, setCustomerFromSuggestion] = useState(false);
    const [customersCache, setCustomersCache] = useState<
      Map<string, CustomerListItem>
    >(new Map());
    const autoSelectAttemptedRef = useRef(false);

    // Invoice fetching state (customerInvoices not needed as we use selectedInvoices)
    const [selectedInvoices, setSelectedInvoices] = useState<SelectedInvoice[]>(
      [],
    );
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [invoicesOffset, setInvoicesOffset] = useState(0);
    const [invoicesHasMore, setInvoicesHasMore] = useState(false);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [invoiceSearchTerm, setInvoiceSearchTerm] = useState<string>("");

    // Advance payment state
    const [advancePayment, setAdvancePayment] = useState<number>(0);
    const [payInFullUsed, setPayInFullUsed] = useState(false);

    // Local input state
    const [bankExchangeRateInput, setBankExchangeRateInput] =
      useState<string>("");
    const [invoiceAmountInputs, setInvoiceAmountInputs] = useState<
      Record<string, string>
    >({});
    const [invoiceAdjustmentInputs, setInvoiceAdjustmentInputs] = useState<
      Record<string, string>
    >({});
    const [advancePaymentInput, setAdvancePaymentInput] = useState<string>("");
    const [isBankExchangeRateFocused, setIsBankExchangeRateFocused] =
      useState(false);
    const [isAdvancePaymentFocused, setIsAdvancePaymentFocused] =
      useState(false);
    const focusedInvoiceInputsRef = useRef<Set<string>>(new Set());

    const handleNumberInputWheel = useCallback(
      (event: React.WheelEvent<HTMLInputElement>) => {
        event.preventDefault();
        event.currentTarget.blur();
      },
      [],
    );

    // Search timeout ref for debouncing
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const prevSearchTermRef = useRef<string>("");

    // Initialize use case
    const invoiceRepository = useMemo(() => new InvoiceRepositoryImpl(), []);
    const getInvoicesUseCase = useMemo(
      () => new GetInvoicesForCustomerUseCase(invoiceRepository),
      [invoiceRepository],
    );

    const getLineItemBaseAmount = useCallback((lineItem: any): number => {
      // const itemTotal = Number(lineItem.item_total);
      // if (!isNaN(itemTotal) && itemTotal > 0) return itemTotal;

      const quantity = Number(lineItem.quantity) || 0;
      const rate = Number(lineItem.rate) || 0;
      return Math.max(0, quantity * rate);
    }, []);

    const buildTdsLineItems = useCallback(
      (lineItems: any[] = []): TdsLineItemSelection[] =>
        lineItems.map((lineItem, index) => {
          const itemTotal = getLineItemBaseAmount(lineItem);
          const tdsPercentage = Number(lineItem.tds_tax_percentage) || 0;
          const tdsAmount =
            tdsPercentage > 0 ? itemTotal * (tdsPercentage / 100) : 0;

          return {
            line_item_id:
              String(lineItem.line_item_id || lineItem.item_id || "") ||
              `line-${index + 1}`,
            line_index: index,
            name: String(lineItem.name || `Line ${index + 1}`),
            description: String(lineItem.description || ""),
            item_total: Math.round(itemTotal * 100) / 100,
            quantity: Number(lineItem.quantity) || 0,
            rate: Number(lineItem.rate ?? lineItem.bcy_rate) || 0,
            tax_name: lineItem.tax_name || undefined,
            tax_percentage: Number(lineItem.tax_percentage) || undefined,
            tds_tax_id: lineItem.tds_tax_id || undefined,
            tds_tax_name: lineItem.tds_tax_name || undefined,
            tds_tax_percentage: tdsPercentage || undefined,
            tds_section: lineItem.tds_section || lineItem.section || undefined,
            tds_tax_amount: Math.round(tdsAmount * 100) / 100,
            is_tds_applied: Number(lineItem.is_tds_applied) === 1,
          };
        }),
      [getLineItemBaseAmount],
    );

    const summarizeLineTds = useCallback(
      (lineItems: TdsLineItemSelection[]) => {
        const selectedLines = lineItems.filter(
          (line) => line.tds_tax_amount > 0,
        );
        const totalTds =
          Math.round(
            selectedLines.reduce((sum, line) => sum + line.tds_tax_amount, 0) *
              100,
          ) / 100;

        if (selectedLines.length === 0 || totalTds <= 0) {
          return {
            tds_tax_amount: 0,
            tds_tax_id: undefined,
            tds_section: undefined,
            tds_tax_percentage: undefined,
            tds_tax_name: undefined,
          };
        }

        const firstLine = selectedLines[0];
        const hasSingleTdsRate = selectedLines.every(
          (line) => line.tds_tax_id === firstLine.tds_tax_id,
        );

        return {
          tds_tax_amount: totalTds,
          tds_tax_id: hasSingleTdsRate ? firstLine.tds_tax_id : "multiple",
          tds_section: hasSingleTdsRate ? firstLine.tds_section : "Multiple",
          tds_tax_percentage: hasSingleTdsRate
            ? firstLine.tds_tax_percentage
            : undefined,
          tds_tax_name: hasSingleTdsRate
            ? firstLine.tds_tax_name
            : "Multiple TDS rates",
        };
      },
      [],
    );

    // Helper function to convert InvoiceListItemEntity to CustomerInvoice
    const convertToCustomerInvoice = (
      entity: InvoiceListItemEntity,
    ): CustomerInvoice => {
      return {
        id: entity.id?.toString() ?? "",
        invoice_id: entity.invoice_id,
        invoice_number: entity.invoice_number,
        date: entity.date,
        total: entity.total,
        balance: entity.balance,
        unprocessed_payment_amount: entity.unprocessed_payment_amount,
        tax_total: entity.tax_total,
        is_tds_applied: entity.is_tds_applied,
        currency_code: entity.currency_code,
        currency_symbol: entity.currency_symbol,
        exchange_rate: entity.exchange_rate,
        status: entity.status,
        tds_tax_amount: entity.tds_tax_amount,
        tds_section: entity.tds_section ?? undefined,
        tds_tax_id: entity.tds_tax_id ?? undefined,
        tds_tax_percentage: entity.tds_tax_percentage,
        tds_tax_name: entity.tds_tax_name ?? undefined,
      };
    };

    // Fetch customers async function for SearchableDropdown
    const fetchCustomers = useCallback(
      async (
        searchTerm: string,
        limit: number,
        offset: number,
        selectedCustomerId?: string,
        selectedCustomerName?: string,
      ): Promise<{
        items: CustomerListItem[];
        hasMore?: boolean;
      }> => {
        const shouldCache = offset === 0 && !searchTerm;
        const cacheKey = { searchTerm, limit, offset };

        if (shouldCache) {
          const cached = getCached<{
            items: CustomerListItem[];
            hasMore?: boolean;
          }>("customers", cacheKey);

          if (cached) {
            if (offset === 0 && selectedCustomerId && selectedCustomerName) {
              const hasSelected = cached.items.some(
                (customer: CustomerListItem) =>
                  customer.zoho_customerid === selectedCustomerId,
              );

              if (!hasSelected) {
                const cachedCustomer = customersCache.get(selectedCustomerId);
                if (cachedCustomer) {
                  return {
                    items: [cachedCustomer, ...cached.items],
                    hasMore: cached.hasMore,
                  };
                }

                try {
                  const selectedResponse =
                    await getCustomerById(selectedCustomerId);
                  if (selectedResponse.data) {
                    const mappedCustomer: CustomerListItem = {
                      id: 0,
                      zoho_customerid: selectedResponse.data.zoho_customerid,
                      customer_id: selectedResponse.data.customer_id,
                      customer_name: selectedResponse.data.customer_name,
                      company_name: selectedResponse.data.customer_name,
                      email: selectedResponse.data.contact_email_1 || "",
                      phone: selectedResponse.data.contact_number_1 || "",
                      status: selectedResponse.data.status,
                      billing_address: selectedResponse.data.billing_address,
                      shipping_address: selectedResponse.data.shipping_address,
                      currency_code:
                        selectedResponse.data.currency_code ??
                        selectedResponse.data.currency_id ??
                        "",
                      currency_symbol:
                        selectedResponse.data.currency_symbol ?? "",
                      payment_terms: selectedResponse.data.payment_terms,
                      credit_limit: selectedResponse.data.credit_limit || 0,
                      created_at: selectedResponse.data.created_at,
                      updated_at: selectedResponse.data.updated_at,
                    };
                    setCustomersCache((prev) => {
                      const newCache = new Map(prev);
                      newCache.set(selectedCustomerId, mappedCustomer);
                      return newCache;
                    });
                    return {
                      items: [mappedCustomer, ...cached.items],
                      hasMore: cached.hasMore,
                    };
                  }
                } catch (error) {
                  console.warn(
                    "Could not fetch selected customer from backend:",
                    error,
                  );
                }
              }
            }
            return cached;
          }
        }

        try {
          const response = await getAllCustomers({
            search: searchTerm || undefined,
            limit,
            offset,
          });

          let items = response.data || [];

          // Filter out excluded customer IDs (already selected in other instances)
          if (excludedCustomerIds && excludedCustomerIds.length > 0) {
            items = items.filter(
              (customer) =>
                !excludedCustomerIds.includes(customer.zoho_customerid),
            );
          }

          if (offset === 0 && selectedCustomerId && selectedCustomerName) {
            const hasSelected = items.some(
              (customer) => customer.zoho_customerid === selectedCustomerId,
            );

            if (!hasSelected) {
              const cachedCustomer = customersCache.get(selectedCustomerId);
              if (cachedCustomer) {
                items = [cachedCustomer, ...items];
              } else {
                try {
                  const selectedResponse =
                    await getCustomerById(selectedCustomerId);
                  if (selectedResponse.data) {
                    const mappedCustomer: CustomerListItem = {
                      id: 0,
                      zoho_customerid: selectedResponse.data.zoho_customerid,
                      customer_id: selectedResponse.data.customer_id,
                      customer_name: selectedResponse.data.customer_name,
                      company_name: selectedResponse.data.customer_name,
                      email: selectedResponse.data.contact_email_1 || "",
                      phone: selectedResponse.data.contact_number_1 || "",
                      status: selectedResponse.data.status,
                      billing_address: selectedResponse.data.billing_address,
                      shipping_address: selectedResponse.data.shipping_address,
                      currency_code:
                        selectedResponse.data.currency_code ??
                        selectedResponse.data.currency_id ??
                        "",
                      currency_symbol:
                        selectedResponse.data.currency_symbol ?? "",
                      payment_terms: selectedResponse.data.payment_terms,
                      credit_limit: selectedResponse.data.credit_limit || 0,
                      created_at: selectedResponse.data.created_at,
                      updated_at: selectedResponse.data.updated_at,
                    };
                    setCustomersCache((prev) => {
                      const newCache = new Map(prev);
                      newCache.set(selectedCustomerId, mappedCustomer);
                      return newCache;
                    });
                    items = [mappedCustomer, ...items];
                  }
                } catch (error) {
                  console.warn(
                    "Could not fetch selected customer from backend:",
                    error,
                  );
                }
              }
            } else {
              const selectedIndex = items.findIndex(
                (customer) => customer.zoho_customerid === selectedCustomerId,
              );
              if (selectedIndex >= 0 && selectedIndex > 0) {
                const [selectedCustomer] = items.splice(selectedIndex, 1);
                items = [selectedCustomer, ...items];
              }
            }
          }

          const result = {
            items,
            hasMore: response.pagination?.has_more || false,
          };

          if (shouldCache) {
            setCached("customers", cacheKey, {
              items: response.data || [],
              hasMore: response.pagination?.has_more || false,
            });
          }

          return result;
        } catch (error) {
          console.error("Error fetching customers:", error);
          return {
            items: [],
            hasMore: false,
          };
        }
      },
      [getCached, setCached, customersCache, excludedCustomerIds],
    );

    // Fetch customer invoices
    const fetchCustomerInvoices = useCallback(
      async (customerZohoId: string, reset: boolean = true) => {
        if (reset) {
          setInvoicesOffset(0);
        }

        setLoadingInvoices(true);
        setInvoicesLoading(true);
        try {
          const result = await getInvoicesUseCase.execute(
            customerZohoId,
            5,
            reset ? 0 : invoicesOffset,
            invoiceSearchTerm || undefined,
          );

          const invoicesWithBalance = result.items
            .filter((invoice) => invoice.balance > 0)
            .map(convertToCustomerInvoice);

          setInvoicesHasMore(result.pagination.has_more);
          setInvoicesOffset(result.pagination.offset + result.pagination.limit);

          const newSelectedInvoices: SelectedInvoice[] =
            invoicesWithBalance.map((invoice) => ({
              invoice,
              amount: 0,
              isSelected: false,
              tds_tax_amount: invoice.tds_tax_amount,
              tds_tax_id: invoice.tds_tax_id,
              tds_section: invoice.tds_section,
              tds_tax_percentage: invoice.tds_tax_percentage,
              tds_tax_name: invoice.tds_tax_name,
              is_tds_applied: invoice.is_tds_applied,
              lineItemsLoaded: false,
              lineItemsLoading: false,
              tds_line_items: [],
            }));

          if (reset) {
            setSelectedInvoices(newSelectedInvoices);
          } else {
            // Deduplicate by invoice_id to prevent duplicates across API calls
            setSelectedInvoices((prev) => {
              const existingInvoiceIds = new Set(
                prev.map((item) => item.invoice.invoice_id),
              );
              const uniqueNewInvoices = newSelectedInvoices.filter(
                (item) => !existingInvoiceIds.has(item.invoice.invoice_id),
              );
              return [...prev, ...uniqueNewInvoices];
            });
          }
        } catch (error) {
          console.error("Failed to fetch customer invoices:", error);
          if (reset) {
            setSelectedInvoices([]);
          }
        } finally {
          setLoadingInvoices(false);
          setInvoicesLoading(false);
        }
      },
      [getInvoicesUseCase, invoicesOffset, invoiceSearchTerm],
    );

    useEffect(() => {
      if (!selectedCustomer) return;

      const currentSearchTerm = invoiceSearchTerm.trim();
      const previousSearchTerm = prevSearchTermRef.current.trim();

      // Skip if search term hasn't actually changed
      if (currentSearchTerm === previousSearchTerm) {
        return;
      }

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (!currentSearchTerm && previousSearchTerm) {
        prevSearchTermRef.current = currentSearchTerm;
        setInvoicesOffset(0);
        fetchCustomerInvoices(selectedCustomer.zoho_customerid, true);
        return;
      }

      if (currentSearchTerm) {
        prevSearchTermRef.current = currentSearchTerm;
        searchTimeoutRef.current = setTimeout(() => {
          if (selectedCustomer.zoho_customerid) {
            setInvoicesOffset(0);
            fetchCustomerInvoices(selectedCustomer.zoho_customerid, true);
          }
        }, 300);
      } else {
        prevSearchTermRef.current = currentSearchTerm;
      }

      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceSearchTerm, selectedCustomer?.zoho_customerid]);

    // Auto-select customer if provided
    useEffect(() => {
      const autoSelectCustomer = async () => {
        if (
          autoSelectCustomerId &&
          autoSelectCustomerId !== "create_new" &&
          !selectedCustomer &&
          !autoSelectAttemptedRef.current
        ) {
          autoSelectAttemptedRef.current = true;
          setLoadingZohoParty(true);
          try {
            const response = await getCustomerById(autoSelectCustomerId);
            if (response.success && response.data) {
              const mappedCustomer: CustomerListItem = {
                id: 0,
                zoho_customerid: response.data.zoho_customerid,
                customer_id: response.data.customer_id,
                customer_name: response.data.customer_name,
                company_name: response.data.customer_name,
                email: response.data.contact_email_1 || "",
                phone: response.data.contact_number_1 || "",
                status: response.data.status,
                billing_address: response.data.billing_address,
                shipping_address: response.data.shipping_address,
                currency_code:
                  response.data.currency_code ??
                  response.data.currency_id ??
                  "",
                currency_symbol: response.data.currency_symbol ?? "",
                payment_terms: response.data.payment_terms,
                credit_limit: response.data.credit_limit || 0,
                created_at: response.data.created_at,
                updated_at: response.data.updated_at,
              };
              setSelectedCustomer(mappedCustomer);
              setCustomerFromSuggestion(true);
              setInvoiceSearchTerm("");
              prevSearchTermRef.current = "";
              setCustomersCache((prev) => {
                const newCache = new Map(prev);
                newCache.set(mappedCustomer.zoho_customerid, mappedCustomer);
                return newCache;
              });
              fetchCustomerInvoices(mappedCustomer.zoho_customerid);
            }
          } catch (error) {
            console.error("Failed to auto-select customer:", error);
          } finally {
            setLoadingZohoParty(false);
            onInitialPartyLoaded?.();
          }
        }
      };
      autoSelectCustomer();
    }, [
      autoSelectCustomerId,
      autoSelectCustomerName,
      selectedCustomer,
      fetchCustomerInvoices,
    ]);

    // Check if invoice is foreign currency
    const isInvoiceForeignCurrency = useCallback(
      (invoice: CustomerInvoice): boolean =>
        isDocumentFxMismatch(invoice.currency_code),
      [],
    );

    // Check if any invoice has foreign currency
    const hasForeignCurrency = useCallback((): boolean => {
      return selectedInvoices.some((item) =>
        isInvoiceForeignCurrency(item.invoice),
      );
    }, [isInvoiceForeignCurrency, selectedInvoices]);

    const getInvoiceAdjustmentLimit = useCallback(
      (item: SelectedInvoice): number => {
        const invoiceBalance = item.invoice.balance || 0;
        const isTdsApplied = item.invoice.is_tds_applied === 1;
        const tdsAmount = isTdsApplied ? 0 : item.tds_tax_amount || 0;
        const remaining =
          invoiceBalance - tdsAmount - Math.max(0, item.amount || 0);
        return Math.max(0, Math.round(remaining * 100) / 100);
      },
      [],
    );

    const shouldShowInvoiceAdjustment = useCallback(
      (item: SelectedInvoice): boolean => {
        if (!item.isSelected) return false;
        if (isInvoiceForeignCurrency(item.invoice)) return false;
        const currentAdjustment = item.adjustmentAmount || 0;
        const adjustmentLimit = getInvoiceAdjustmentLimit(item);
        return (
          currentAdjustment > 0 ||
          (adjustmentLimit > 0 && adjustmentLimit <= 10)
        );
      },
      [getInvoiceAdjustmentLimit, isInvoiceForeignCurrency],
    );

    // Get maximum amount that can be entered for a specific invoice
    const getMaxAmountForInvoice = useCallback(
      (invoiceId: string): number => {
        const currentInvoice = selectedInvoices.find(
          (item) => item.invoice.invoice_id === invoiceId,
        );

        if (!currentInvoice) return 0;

        const isForeign = isInvoiceForeignCurrency(currentInvoice.invoice);

        if (isForeign) {
          return Math.max(
            0,
            Math.round((currentInvoice.invoice.balance || 0) * 100) / 100,
          );
        }

        // For multiple instances, consider totalAllocatedAmountsFromOtherInstances
        const totalFromOtherInstances =
          isMultipleInstances && totalAllocatedAmountsFromOtherInstances
            ? totalAllocatedAmountsFromOtherInstances
            : 0;

        const otherInvoicesInCurrentInstance = selectedInvoices
          .filter(
            (item) => item.isSelected && item.invoice.invoice_id !== invoiceId,
          )
          .reduce((sum, item) => {
            return sum + (item.amount || 0);
          }, 0);

        const availableAmount =
          transactionAmount -
          totalFromOtherInstances -
          otherInvoicesInCurrentInstance;

        const invoiceBalance = currentInvoice.invoice.balance || 0;
        const tdsAmount = currentInvoice.tds_tax_amount || 0;
        const isTdsApplied = currentInvoice.invoice.is_tds_applied === 1;

        const maxFromInvoice = isTdsApplied
          ? invoiceBalance
          : invoiceBalance - tdsAmount;

        const maxFromTransactionRounded =
          Math.round(availableAmount * 100) / 100;
        const maxFromInvoiceRounded = Math.round(maxFromInvoice * 100) / 100;

        return Math.max(
          0,
          Math.min(maxFromTransactionRounded, maxFromInvoiceRounded),
        );
      },
      [
        isInvoiceForeignCurrency,
        selectedInvoices,
        isMultipleInstances,
        totalAllocatedAmountsFromOtherInstances,
        transactionAmount,
      ],
    );

    // Calculate advance payment
    const calculateAdvancePayment = useCallback((): number => {
      if (isMultipleInstances) {
        // For multiple instances, advance payment is manual - return current value
        return advancePayment;
      }

      // Auto-calculate for single instance
      const hasForeign = hasForeignCurrency();
      if (!hasForeign || !bankExchangeRate || bankExchangeRate <= 0) {
        const totalUsedAmount = selectedInvoices
          .filter((item) => item.isSelected)
          .reduce((sum, item) => {
            return sum + (item.amount || 0);
          }, 0);
        return Math.max(0, transactionAmount - totalUsedAmount);
      }

      // Foreign currency calculation
      const totalUsedAmount = selectedInvoices
        .filter((item) => item.isSelected)
        .reduce((sum, item) => {
          return sum + (item.amount || 0);
        }, 0);

      if (totalUsedAmount === 0) {
        return transactionAmount / bankExchangeRate;
      }

      const remainingAdvance =
        transactionAmount / bankExchangeRate - totalUsedAmount;
      return Math.max(0, Math.round(remainingAdvance * 100) / 100);
    }, [
      isMultipleInstances,
      advancePayment,
      hasForeignCurrency,
      bankExchangeRate,
      selectedInvoices,
      transactionAmount,
    ]);

    // Track previous isMultipleInstances to detect transition
    const prevIsMultipleInstancesRef = useRef(isMultipleInstances);

    // Update advance payment when needed
    useEffect(() => {
      // When transitioning from single to multiple instances, clear advance payment
      if (isMultipleInstances && !prevIsMultipleInstancesRef.current) {
        setAdvancePayment(0);
        setAdvancePaymentInput("");
      }

      prevIsMultipleInstancesRef.current = isMultipleInstances;

      if (!isMultipleInstances) {
        const calculated = calculateAdvancePayment();
        setAdvancePayment((prev) =>
          Math.abs(calculated - prev) > 0.01 ? calculated : prev,
        );
      }
    }, [
      isMultipleInstances,
      calculateAdvancePayment,
      selectedInvoices,
      transactionAmount,
      bankExchangeRate,
    ]);

    useEffect(() => {
      setSelectedInvoices((prev) => {
        let changed = false;
        const next = prev.map((item) => {
          if (
            !isInvoiceForeignCurrency(item.invoice) ||
            !item.adjustmentAmount
          ) {
            return item;
          }

          changed = true;
          return {
            ...item,
            adjustmentAmount: 0,
          };
        });

        return changed ? next : prev;
      });
    }, [isInvoiceForeignCurrency, selectedInvoices]);

    // Notify parent of changes
    const onChangeRef = useRef(onChange);
    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      const totalInvoiceAmounts = selectedInvoices
        .filter((item) => item.isSelected)
        .reduce((sum, item) => sum + (item.amount || 0), 0);

      onChangeRef.current({
        customerId: selectedCustomer?.zoho_customerid || null,
        customerName: selectedCustomer?.customer_name || null,
        selectedInvoices: selectedInvoices,
        advancePayment: knockOffOpeningBalance ? 0 : advancePayment,
        onAccountAmount: knockOffOpeningBalance ? advancePayment : 0,
        totalInvoiceAmounts: totalInvoiceAmounts,
        bankExchangeRate: bankExchangeRate,
      });
    }, [
      selectedCustomer,
      selectedInvoices,
      advancePayment,
      bankExchangeRate,
      knockOffOpeningBalance,
    ]);

    // Handle customer selection
    const handleCustomerSelect = useCallback(
      (customer: CustomerListItem) => {
        setCustomerFromSuggestion(false); // User changed; no longer showing recommendation
        setSelectedInvoices([]);
        setInvoicesHasMore(false);
        setInvoicesOffset(0);
        setLoadingInvoices(true);
        setInvoicesLoading(true);
        setInvoiceSearchTerm("");
        prevSearchTermRef.current = "";
        onBankExchangeRateChange(null);

        setSelectedCustomer(customer);
        setCustomersCache((prev) => {
          const newCache = new Map(prev);
          newCache.set(customer.zoho_customerid, customer);
          return newCache;
        });
        fetchCustomerInvoices(customer.zoho_customerid);
      },
      [fetchCustomerInvoices, onBankExchangeRateChange],
    );

    const fetchInvoiceLineItems = useCallback(
      async (invoiceId: string) => {
        setSelectedInvoices((prev) =>
          prev.map((item) =>
            item.invoice.invoice_id === invoiceId
              ? { ...item, lineItemsLoading: true }
              : item,
          ),
        );

        try {
          const invoiceDetails =
            await invoiceRepository.getZohoInvoiceDetails(invoiceId);
          const tdsLineItems = buildTdsLineItems(
            invoiceDetails.line_items || [],
          );
          const tdsSummary = summarizeLineTds(tdsLineItems);

          setSelectedInvoices((prev) =>
            prev.map((item) =>
              item.invoice.invoice_id === invoiceId
                ? {
                    ...item,
                    invoice: {
                      ...item.invoice,
                      tax_total: invoiceDetails.tax_total,
                      line_items: invoiceDetails.line_items,
                    },
                    lineItemsLoaded: true,
                    lineItemsLoading: false,
                    tds_line_items: tdsLineItems,
                    tds_tax_amount:
                      tdsSummary.tds_tax_amount || item.tds_tax_amount,
                    tds_tax_id: tdsSummary.tds_tax_id || item.tds_tax_id,
                    tds_section: tdsSummary.tds_section || item.tds_section,
                    tds_tax_percentage:
                      tdsSummary.tds_tax_percentage || item.tds_tax_percentage,
                    tds_tax_name: tdsSummary.tds_tax_name || item.tds_tax_name,
                  }
                : item,
            ),
          );
        } catch (error) {
          console.error("Failed to fetch invoice line items:", error);
          setSelectedInvoices((prev) =>
            prev.map((item) =>
              item.invoice.invoice_id === invoiceId
                ? {
                    ...item,
                    lineItemsLoaded: true,
                    lineItemsLoading: false,
                    tds_line_items: [],
                  }
                : item,
            ),
          );
        }
      },
      [buildTdsLineItems, invoiceRepository, summarizeLineTds],
    );

    // Handle invoice selection
    const handleInvoiceSelect = useCallback(
      (invoiceId: string) => {
        const currentInvoice = selectedInvoices.find(
          (item) => item.invoice.invoice_id === invoiceId,
        );
        const shouldFetchLineItems = !!(
          currentInvoice &&
          !currentInvoice.isSelected &&
          !currentInvoice.lineItemsLoaded &&
          !currentInvoice.lineItemsLoading
        );

        setSelectedInvoices((prev) => {
          const updated = prev.map((item) => {
            if (item.invoice.invoice_id === invoiceId) {
              const newIsSelected = !item.isSelected;
              return {
                ...item,
                isSelected: newIsSelected,
                amount: newIsSelected ? item.amount : 0,
                adjustmentAmount: newIsSelected ? item.adjustmentAmount : 0,
              };
            }
            return item;
          });

          const hasNewlySelectedWithoutAmount = updated.some(
            (item) => item.isSelected && (item.amount === 0 || !item.amount),
          );

          if (hasNewlySelectedWithoutAmount) {
            setPayInFullUsed(false);
          }

          return updated;
        });

        if (shouldFetchLineItems) {
          fetchInvoiceLineItems(invoiceId);
        }
      },
      [fetchInvoiceLineItems, selectedInvoices],
    );

    // Handle amount change
    const handleAmountChange = useCallback(
      (invoiceId: string, amount: number) => {
        setSelectedInvoices((prev) => {
          const invoiceItem = prev.find(
            (item) => item.invoice.invoice_id === invoiceId,
          );

          if (!invoiceItem) return prev;

          const roundedAmount = Math.round(amount * 100) / 100;
          const maxAllowed = getMaxAmountForInvoice(invoiceId);
          const roundedMaxAllowed = Math.round(maxAllowed * 100) / 100;

          const isInvoiceForeign = isInvoiceForeignCurrency(
            invoiceItem.invoice,
          );
          let finalAmount = roundedAmount;

          if (isInvoiceForeign) {
            const invoiceBalance = invoiceItem.invoice.balance || 0;
            const roundedInvoiceBalance =
              Math.round(invoiceBalance * 100) / 100;
            if (finalAmount > roundedInvoiceBalance + 0.001) {
              showWarning(
                `Amount cannot exceed ${
                  invoiceItem.invoice.currency_symbol || "₹"
                }${formatAmount(roundedInvoiceBalance)}.`,
              );
              finalAmount = roundedInvoiceBalance;
            }
          } else {
            if (finalAmount > roundedMaxAllowed + 0.001) {
              const currencySymbol = invoiceItem.invoice.currency_symbol || "₹";
              const isTdsApplied = invoiceItem.invoice.is_tds_applied === 1;
              const tdsInfo =
                !isTdsApplied && invoiceItem.tds_tax_amount
                  ? ` (TDS of ${currencySymbol}${formatAmount(
                      invoiceItem.tds_tax_amount,
                    )} will be deducted separately)`
                  : "";
              showWarning(
                `Amount cannot exceed ${currencySymbol}${formatAmount(
                  roundedMaxAllowed,
                )}${tdsInfo}.`,
              );
              finalAmount = roundedMaxAllowed;
            }

            const isTdsApplied = invoiceItem.invoice.is_tds_applied === 1;
            const tdsAmount = invoiceItem.tds_tax_amount || 0;
            const roundedTdsAmount = Math.round(tdsAmount * 100) / 100;
            const invoiceBalance = invoiceItem.invoice.balance || 0;
            const roundedInvoiceBalance =
              Math.round(invoiceBalance * 100) / 100;
            const maxWithoutBreachingBalance = isTdsApplied
              ? roundedInvoiceBalance
              : roundedInvoiceBalance - roundedTdsAmount;
            if (finalAmount > maxWithoutBreachingBalance + 0.001) {
              showWarning(
                `Payment + TDS cannot exceed invoice balance of ${
                  invoiceItem.invoice.currency_symbol || "₹"
                }${formatAmount(roundedInvoiceBalance)}.`,
              );
              finalAmount = Math.max(0, maxWithoutBreachingBalance);
            }
          }

          const roundedFinalAmount = Math.round(finalAmount * 100) / 100;
          const updated = prev.map((item) => {
            if (item.invoice.invoice_id !== invoiceId) {
              return item;
            }

            const invoiceBalance = item.invoice.balance || 0;
            const isTdsApplied = item.invoice.is_tds_applied === 1;
            const tdsAmount = isTdsApplied ? 0 : item.tds_tax_amount || 0;
            const remaining =
              Math.round(
                (invoiceBalance - tdsAmount - roundedFinalAmount) * 100,
              ) / 100;
            const adjustmentAmount =
              !isInvoiceForeignCurrency(item.invoice) &&
              remaining > 0 &&
              remaining <= 10
                ? remaining
                : 0;

            return {
              ...item,
              amount: roundedFinalAmount,
              adjustmentAmount,
            };
          });

          setPayInFullUsed(false);

          return updated;
        });
      },
      [getMaxAmountForInvoice, isInvoiceForeignCurrency, showWarning],
    );

    const handleAdjustmentChange = useCallback(
      (invoiceId: string, amount: number) => {
        const currentItem = selectedInvoices.find(
          (item) => item.invoice.invoice_id === invoiceId,
        );
        const isForeignInvoice = currentItem
          ? isInvoiceForeignCurrency(currentItem.invoice)
          : false;
        const adjustmentLimit =
          currentItem && !isForeignInvoice
            ? getInvoiceAdjustmentLimit(currentItem)
            : 0;
        const roundedAmount = Math.round(Math.max(0, amount) * 100) / 100;
        const finalAmount = isForeignInvoice
          ? 0
          : Math.min(roundedAmount, adjustmentLimit);
        const previousAdjustment = currentItem?.adjustmentAmount || 0;

        if (
          currentItem &&
          !isForeignInvoice &&
          roundedAmount > adjustmentLimit + 0.001
        ) {
          showWarning(
            `Adjustment cannot exceed ${
              currentItem.invoice.currency_symbol || "₹"
            }${adjustmentLimit.toFixed(2)}.`,
          );
        }

        setSelectedInvoices((prev) =>
          prev.map((item) => {
            if (item.invoice.invoice_id !== invoiceId) return item;

            return {
              ...item,
              adjustmentAmount: finalAmount,
            };
          }),
        );

        if (
          isMultipleInstances &&
          (canAdvance || knockOffOpeningBalance) &&
          previousAdjustment > 0 &&
          previousAdjustment <= 10 &&
          finalAmount === 0
        ) {
          setAdvancePayment(
            (prevAdvance) =>
              Math.round((prevAdvance + previousAdjustment) * 100) / 100,
          );
        }
      },
      [
        canAdvance,
        knockOffOpeningBalance,
        getInvoiceAdjustmentLimit,
        isInvoiceForeignCurrency,
        isMultipleInstances,
        selectedInvoices,
        showWarning,
      ],
    );

    const clearInvoiceInput = useCallback((invoiceId: string) => {
      setInvoiceAmountInputs((prev) => {
        if (!(invoiceId in prev)) return prev;
        const next = { ...prev };
        delete next[invoiceId];
        return next;
      });
    }, []);

    const handleLineTdsSelect = useCallback(
      (invoiceId: string, lineItemId: string, tdsRate: TDSRate | null) => {
        let recalculatedPayInFull = false;

        setSelectedInvoices((prev) =>
          prev.map((item) => {
            if (item.invoice.invoice_id !== invoiceId) return item;

            if (item.is_tds_applied === 1) {
              return item;
            }

            const updatedLineItems = (item.tds_line_items || []).map((line) => {
              if (line.line_item_id !== lineItemId) return line;

              if (!tdsRate) {
                return {
                  ...line,
                  tds_tax_id: undefined,
                  tds_tax_name: undefined,
                  tds_tax_percentage: undefined,
                  tds_section: undefined,
                  tds_tax_amount: 0,
                };
              }

              const tdsAmount =
                line.item_total * (tdsRate.tds_tax_percentage / 100);

              return {
                ...line,
                tds_tax_id: tdsRate.tds_tax_id,
                tds_tax_name: tdsRate.tds_tax_name,
                tds_tax_percentage: tdsRate.tds_tax_percentage,
                tds_section: tdsRate.section,
                tds_tax_amount: Math.round(tdsAmount * 100) / 100,
              };
            });
            const tdsSummary = summarizeLineTds(updatedLineItems);

            const invoiceBalance = item.invoice.balance || 0;
            const previousTds = item.tds_tax_amount || 0;
            const previousAmount = item.amount || 0;
            const wasEffectivelyPayInFull =
              (payInFullUsed && previousAmount > 0) ||
              (previousAmount > 0 &&
                Math.abs(previousAmount - (invoiceBalance - previousTds)) <
                  0.01);

            let newAmount = 0;
            let newAdjustment = 0;

            if (wasEffectivelyPayInFull) {
              recalculatedPayInFull = true;
              const newTds = tdsSummary.tds_tax_amount || 0;
              const fullPayAmount = Math.max(
                0,
                Math.round((invoiceBalance - newTds) * 100) / 100,
              );

              const totalFromOtherInstances =
                isMultipleInstances && totalAllocatedAmountsFromOtherInstances
                  ? totalAllocatedAmountsFromOtherInstances
                  : 0;
              const otherInvoicesAmount = prev
                .filter(
                  (invoiceItem) =>
                    invoiceItem.isSelected &&
                    invoiceItem.invoice.invoice_id !== invoiceId,
                )
                .reduce(
                  (sum, invoiceItem) => sum + (invoiceItem.amount || 0),
                  0,
                );
              const availableFromTransaction =
                Math.round(
                  (transactionAmount -
                    totalFromOtherInstances -
                    otherInvoicesAmount) *
                    100,
                ) / 100;
              const maxAllowed = Math.max(
                0,
                Math.min(availableFromTransaction, fullPayAmount),
              );
              newAmount = Math.min(fullPayAmount, maxAllowed);

              const remaining =
                Math.round((invoiceBalance - newTds - newAmount) * 100) / 100;
              newAdjustment =
                !isInvoiceForeignCurrency(item.invoice) &&
                remaining > 0 &&
                remaining <= 10
                  ? remaining
                  : 0;
            }

            return {
              ...item,
              amount: wasEffectivelyPayInFull ? newAmount : 0,
              adjustmentAmount: wasEffectivelyPayInFull ? newAdjustment : 0,
              tds_line_items: updatedLineItems,
              tds_tax_amount: tdsSummary.tds_tax_amount,
              tds_tax_id: tdsSummary.tds_tax_id,
              tds_section: tdsSummary.tds_section,
              tds_tax_percentage: tdsSummary.tds_tax_percentage,
              tds_tax_name: tdsSummary.tds_tax_name,
            };
          }),
        );

        if (!recalculatedPayInFull) {
          clearInvoiceInput(invoiceId);
          setPayInFullUsed(false);
        }
      },
      [
        clearInvoiceInput,
        summarizeLineTds,
        payInFullUsed,
        isInvoiceForeignCurrency,
        isMultipleInstances,
        totalAllocatedAmountsFromOtherInstances,
        transactionAmount,
      ],
    );

    // Handle Pay in Full
    const handlePayInFull = useCallback(() => {
      if (hasForeignCurrency()) {
        showWarning(
          "Pay in Full is unavailable for foreign currency invoices.",
        );
        return;
      }
      const transactionAmountINR = transactionAmount;
      const isForeignCurrencyTransaction = hasForeignCurrency();
      const transactionAmountForCalculation =
        isForeignCurrencyTransaction && bankExchangeRate && bankExchangeRate > 0
          ? transactionAmountINR / bankExchangeRate
          : transactionAmountINR;

      if (
        isForeignCurrencyTransaction &&
        (!bankExchangeRate || bankExchangeRate <= 0)
      ) {
        showWarning(
          "Please enter the Bank Exchange Rate before using Pay in Full for foreign currency transactions.",
        );
        return;
      }

      const selectedItemsWithoutAmounts = selectedInvoices.filter(
        (item) => item.isSelected && (item.amount === 0 || !item.amount),
      );

      if (selectedItemsWithoutAmounts.length === 0) {
        showWarning(
          "Please select at least one invoice without an amount to pay in full.",
        );
        return;
      }

      let remainingAmount: number;
      const totalFromOtherInstances =
        isMultipleInstances && totalAllocatedAmountsFromOtherInstances
          ? totalAllocatedAmountsFromOtherInstances
          : 0;

      if (
        isForeignCurrencyTransaction &&
        bankExchangeRate &&
        bankExchangeRate > 0
      ) {
        const currentlyUsedAmountINR = selectedInvoices
          .filter((item) => item.isSelected && item.amount > 0)
          .reduce((sum, item) => {
            const itemAmount = item.amount || 0;
            return sum + itemAmount * bankExchangeRate;
          }, 0);
        const remainingAmountINR = Math.max(
          0,
          transactionAmountINR - currentlyUsedAmountINR,
        );
        remainingAmount =
          remainingAmountINR > 0
            ? Math.floor((remainingAmountINR / bankExchangeRate) * 100) / 100
            : 0;
      } else {
        const currentlyUsedAmount = selectedInvoices
          .filter((item) => item.isSelected && item.amount > 0)
          .reduce((sum, item) => {
            const itemAmount = item.amount || 0;
            return sum + itemAmount;
          }, 0);
        remainingAmount = Math.max(
          0,
          transactionAmountForCalculation -
            totalFromOtherInstances -
            currentlyUsedAmount,
        );
      }

      const invoicesToUpdate: Array<{
        invoiceId: string;
        amount: number;
      }> = [];
      const invoicePlans = selectedItemsWithoutAmounts.map((item) => {
        const invoiceBalance = item.invoice.balance || 0;
        const isTdsApplied = item.invoice.is_tds_applied === 1;
        const tdsAmount =
          !isTdsApplied && item.tds_tax_amount ? item.tds_tax_amount : 0;
        const effectiveInvoiceBalance = isTdsApplied
          ? invoiceBalance
          : Math.max(0, invoiceBalance - tdsAmount);

        return {
          invoiceId: item.invoice.invoice_id,
          effectiveInvoiceBalance:
            Math.round(effectiveInvoiceBalance * 100) / 100,
          adjustmentCapacity: isInvoiceForeignCurrency(item.invoice)
            ? 0
            : Math.min(
                10,
                Math.max(0, Math.round(effectiveInvoiceBalance * 100) / 100),
              ),
        };
      });
      const totalEffectiveInvoiceBalance = invoicePlans.reduce(
        (sum, plan) => sum + plan.effectiveInvoiceBalance,
        0,
      );
      const totalAdjustmentNeeded =
        Math.round(
          Math.max(0, totalEffectiveInvoiceBalance - remainingAmount) * 100,
        ) / 100;
      const totalAdjustmentCapacity = invoicePlans.reduce(
        (sum, plan) => sum + plan.adjustmentCapacity,
        0,
      );
      const payInFullAdjustmentsByInvoiceId: Record<string, number> = {};

      if (
        totalAdjustmentNeeded > 0 &&
        totalAdjustmentNeeded <= totalAdjustmentCapacity
      ) {
        let remainingAdjustment = totalAdjustmentNeeded;
        invoicePlans.forEach((plan, index) => {
          if (remainingAdjustment <= 0) return;
          const remainingPlans = invoicePlans.length - index;
          const share =
            index === invoicePlans.length - 1
              ? remainingAdjustment
              : Math.floor((remainingAdjustment / remainingPlans) * 100) / 100;
          const adjustment = Math.min(plan.adjustmentCapacity, share);
          payInFullAdjustmentsByInvoiceId[plan.invoiceId] = adjustment;
          remainingAdjustment =
            Math.round((remainingAdjustment - adjustment) * 100) / 100;
        });
      }
      const cannotPayFull: Array<{
        invoiceNumber: string;
        invoiceBalance: number;
        requiredAmount: number;
      }> = [];

      for (const item of selectedItemsWithoutAmounts) {
        if (remainingAmount <= 0) {
          cannotPayFull.push({
            invoiceNumber: item.invoice.invoice_number,
            invoiceBalance: item.invoice.balance || 0,
            requiredAmount: item.invoice.balance || 0,
          });
          continue;
        }

        const invoiceBalance = item.invoice.balance || 0;
        // Account for TDS that will be deducted separately (same logic as getMaxAmountForInvoice)
        const isTdsApplied = item.invoice.is_tds_applied === 1;
        const tdsAmount =
          !isTdsApplied && item.tds_tax_amount ? item.tds_tax_amount : 0;
        const effectiveInvoiceBalance = isTdsApplied
          ? invoiceBalance
          : Math.max(0, invoiceBalance - tdsAmount);
        const plannedAdjustment =
          payInFullAdjustmentsByInvoiceId[item.invoice.invoice_id] || 0;

        let paymentAmount = Math.min(
          Math.max(0, effectiveInvoiceBalance - plannedAdjustment),
          remainingAmount,
        );

        if (
          isForeignCurrencyTransaction &&
          bankExchangeRate &&
          bankExchangeRate > 0 &&
          remainingAmount > 0
        ) {
          const remainingAmountINR = remainingAmount * bankExchangeRate;
          const maxPaymentAmount =
            Math.floor((remainingAmountINR / bankExchangeRate) * 100) / 100;
          paymentAmount = Math.min(paymentAmount, maxPaymentAmount);
        }

        paymentAmount = Math.round(paymentAmount * 100) / 100;

        if (paymentAmount > 0) {
          invoicesToUpdate.push({
            invoiceId: item.invoice.invoice_id,
            amount: paymentAmount,
          });

          if (
            isForeignCurrencyTransaction &&
            bankExchangeRate &&
            bankExchangeRate > 0
          ) {
            const currentlyUsedAmountINR = selectedInvoices
              .filter((bill) => {
                const isAlreadyInUpdate = invoicesToUpdate.some(
                  (u) => u.invoiceId === bill.invoice.invoice_id,
                );
                return (
                  (bill.isSelected && bill.amount > 0 && !isAlreadyInUpdate) ||
                  isAlreadyInUpdate
                );
              })
              .reduce((sum, bill) => {
                const updateInfo = invoicesToUpdate.find(
                  (u) => u.invoiceId === bill.invoice.invoice_id,
                );
                const billAmount = updateInfo
                  ? updateInfo.amount
                  : bill.amount || 0;
                return sum + billAmount * bankExchangeRate;
              }, 0);
            const remainingAmountINR = Math.max(
              0,
              transactionAmountINR - currentlyUsedAmountINR,
            );
            remainingAmount =
              remainingAmountINR > 0
                ? Math.floor((remainingAmountINR / bankExchangeRate) * 100) /
                  100
                : 0;
          } else {
            remainingAmount -= paymentAmount;
            remainingAmount = Math.round(remainingAmount * 100) / 100;
          }
        } else {
          cannotPayFull.push({
            invoiceNumber: item.invoice.invoice_number,
            invoiceBalance: invoiceBalance,
            requiredAmount: invoiceBalance,
          });
        }
      }

      if (cannotPayFull.length > 0) {
        const invoiceNumbers = cannotPayFull
          .map((item) => item.invoiceNumber)
          .join(", ");
        showWarning(
          `The following invoices cannot be paid due to insufficient transaction amount: ${invoiceNumbers}. Please deselect them or reduce the amount manually.`,
        );

        if (invoicesToUpdate.length === 0) {
          return;
        }
      }

      setSelectedInvoices((prev) => {
        const updated = prev.map((item) => {
          const updateInfo = invoicesToUpdate.find(
            (u) => u.invoiceId === item.invoice.invoice_id,
          );
          if (updateInfo) {
            const invoiceBalance = item.invoice.balance || 0;
            const isTdsApplied = item.invoice.is_tds_applied === 1;
            const tdsAmount = isTdsApplied ? 0 : item.tds_tax_amount || 0;
            const remaining =
              Math.round(
                (invoiceBalance - tdsAmount - updateInfo.amount) * 100,
              ) / 100;
            const adjustmentAmount =
              !isInvoiceForeignCurrency(item.invoice) &&
              remaining > 0 &&
              remaining <= 10
                ? remaining
                : 0;
            return {
              ...item,
              amount: updateInfo.amount,
              adjustmentAmount,
            };
          }
          return item;
        });

        return updated;
      });

      if (invoicesToUpdate.length > 0) {
        if (cannotPayFull.length === 0) {
          // showSuccess("Applied payment amounts to selected invoices.");
        } else {
          // showSuccess(
          //   `Applied payment amounts to ${invoicesToUpdate.length} invoice(s).`
          // );
        }
      }

      setPayInFullUsed(true);
    }, [
      hasForeignCurrency,
      isInvoiceForeignCurrency,
      transactionAmount,
      bankExchangeRate,
      selectedInvoices,
      isMultipleInstances,
      totalAllocatedAmountsFromOtherInstances,
      showWarning,
    ]);

    // Load more invoices
    const loadMoreInvoices = useCallback(() => {
      if (!invoicesHasMore || invoicesLoading || !selectedCustomer) return;
      fetchCustomerInvoices(selectedCustomer.zoho_customerid, false);
    }, [
      invoicesHasMore,
      invoicesLoading,
      selectedCustomer,
      fetchCustomerInvoices,
    ]);

    // Helper functions
    const truncateText = (text: string, maxLength: number): string => {
      if (text.length <= maxLength) {
        return text;
      }
      return text.substring(0, maxLength) + "...";
    };

    const formatIndianNumber = (amount: number): string => {
      const sign = amount < 0 ? "-" : "";
      const absoluteAmount = Math.abs(amount);
      const parts = absoluteAmount.toFixed(2).split(".");
      const integerPart = parts[0];
      const decimalPart = parts[1];
      let formatted = "";
      const reversed = integerPart.split("").reverse();

      for (let i = 0; i < reversed.length; i++) {
        if (i === 3) {
          formatted = "," + formatted;
        } else if (i > 3 && (i - 3) % 2 === 0) {
          formatted = "," + formatted;
        }
        formatted = reversed[i] + formatted;
      }

      return sign + formatted + "." + decimalPart;
    };

    const formatAmount = (amount: number): string => {
      return formatIndianNumber(amount);
    };

    // Calculate forex details
    const calculateForexDetails = () => {
      if (!hasForeignCurrency() || !bankExchangeRate || bankExchangeRate <= 0) {
        return {
          forexGainOrLoss: 0,
          bankCharges: 0,
          invoiceINRTotal: 0,
          realisationINr: 0,
        };
      }

      const invoiceINRTotal = selectedInvoices
        .filter((item) => item.isSelected && item.amount > 0)
        .reduce((sum, item) => {
          const invoiceRate = item.invoice.exchange_rate || 1;
          return sum + item.amount * invoiceRate;
        }, 0);

      const realisationINr = selectedInvoices
        .filter((item) => item.isSelected && item.amount > 0)
        .reduce((sum, item) => {
          return sum + item.amount * bankExchangeRate;
        }, 0);

      const forexGainOrLoss = realisationINr - invoiceINRTotal;
      const bankCharges = realisationINr - transactionAmount;

      return {
        forexGainOrLoss: Math.round(forexGainOrLoss * 100) / 100,
        bankCharges: Math.round(bankCharges * 100) / 100,
        invoiceINRTotal: Math.round(invoiceINRTotal * 100) / 100,
        realisationINr: Math.round(realisationINr * 100) / 100,
      };
    };

    const forexDetails = calculateForexDetails();

    // Sync local input states with prop values when they change externally
    useEffect(() => {
      if (isBankExchangeRateFocused) {
        return;
      }
      if (bankExchangeRate !== null) {
        const stringValue = bankExchangeRate.toString();
        const currentParsed = parseFloat(bankExchangeRateInput);
        if (isNaN(currentParsed) || currentParsed !== bankExchangeRate) {
          setBankExchangeRateInput(stringValue);
        }
      } else if (bankExchangeRateInput !== "") {
        setBankExchangeRateInput("");
      }
    }, [bankExchangeRate, isBankExchangeRateFocused]);

    useEffect(() => {
      if (!isAdvancePaymentFocused) {
        if (advancePayment > 0) {
          const stringValue = advancePayment.toString();
          const currentParsed = parseFloat(advancePaymentInput);
          if (isNaN(currentParsed) || currentParsed !== advancePayment) {
            setAdvancePaymentInput(stringValue);
          }
        } else if (advancePaymentInput !== "") {
          setAdvancePaymentInput("");
        }
      }
    }, [advancePayment, isAdvancePaymentFocused]);

    // Sync invoice amount/adjustment inputs when they change externally (e.g. Pay in Full)
    useEffect(() => {
      setInvoiceAmountInputs((prev) => {
        let next: Record<string, string> | null = null;
        selectedInvoices.forEach((item) => {
          if (!item.isSelected || item.amount <= 0) return;
          const invoiceId = item.invoice.invoice_id;
          if (focusedInvoiceInputsRef.current.has(invoiceId)) return;
          const existingInput = (next ?? prev)[invoiceId];
          const currentParsed = parseFloat(existingInput || "0");
          if (currentParsed !== item.amount || !existingInput) {
            if (!next) next = { ...prev };
            next[invoiceId] = item.amount.toString();
          }
        });
        return next ?? prev;
      });
    }, [selectedInvoices]);

    useEffect(() => {
      setInvoiceAdjustmentInputs((prev) => {
        let next: Record<string, string> | null = null;
        selectedInvoices.forEach((item) => {
          const invoiceId = item.invoice.invoice_id;
          if (!item.isSelected || !shouldShowInvoiceAdjustment(item)) {
            if (prev[invoiceId] !== undefined) {
              if (!next) next = { ...prev };
              delete next[invoiceId];
            }
            return;
          }
          const adjustmentAmount = item.adjustmentAmount || 0;
          const existingInput = (next ?? prev)[invoiceId];
          const currentParsed = parseFloat(existingInput || "0");
          if (currentParsed !== adjustmentAmount || !existingInput) {
            if (!next) next = { ...prev };
            next[invoiceId] =
              adjustmentAmount > 0 ? adjustmentAmount.toString() : "";
          }
        });
        return next ?? prev;
      });
    }, [selectedInvoices, shouldShowInvoiceAdjustment]);

    return (
      <div ref={ref} className="flex w-full flex-col items-start gap-4 mb-6">
        {/* Customer Selection */}
        <div className="flex w-full items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-body font-body text-default-font">
              Customer
            </span>
            <SearchableDropdown
              fetchItems={(searchTerm, limit, offset) =>
                fetchCustomers(
                  searchTerm,
                  limit,
                  offset,
                  selectedCustomer?.zoho_customerid,
                  selectedCustomer?.customer_name,
                )
              }
              trigger={
                <Button
                  className="h-8 w-40 flex-none justify-between overflow-hidden"
                  variant="neutral-secondary"
                  iconRight={
                    loadingZohoParty ? (
                      <FeatherLoader className="h-4 w-4 animate-spin" />
                    ) : (
                      <FeatherChevronDown />
                    )
                  }
                  disabled={loadingZohoParty}
                >
                  <span className="truncate text-left">
                    {loadingZohoParty
                      ? "Loading customer..."
                      : selectedCustomer
                        ? truncateText(selectedCustomer.customer_name, 12)
                        : "Select Customer"}
                  </span>
                </Button>
              }
              getSearchableText={(customer) => customer.customer_name}
              renderItem={(customer) => customer.customer_name}
              getItemKey={(customer) => customer.zoho_customerid}
              onSelect={handleCustomerSelect}
              selectedKey={selectedCustomer?.zoho_customerid}
              searchPlaceholder="Search customers..."
              emptyMessage="No customers found"
              noResultsMessage="No customers match your search"
              initialLimit={20}
            />
            {customerFromSuggestion && autoSelectCustomerId && (
              <div className="flex items-center gap-1">
                <Badge variant="brand">Recommended</Badge>
                {recommendationSource && (
                  <Badge variant="neutral">
                    {(() => {
                      switch (recommendationSource) {
                        case "knn":
                          return "K-NN";
                        case "override":
                          return "Override";
                        case "direct_match":
                          return "Direct match";
                        case "amount_match":
                          return "Amount";
                        case "ledger_match":
                          return "Ledger";
                        case "vendormatching":
                          return "LLM";
                        default:
                          return recommendationSource;
                      }
                    })()}
                    {recommendationConfidence != null &&
                      recommendationConfidence !== "" &&
                      ` · ${Math.round(
                        (typeof recommendationConfidence === "string"
                          ? parseFloat(recommendationConfidence)
                          : recommendationConfidence) * 100,
                      )}%`}
                  </Badge>
                )}
                {recommendationSource && (
                  <SubframeCore.Tooltip.Provider>
                    <SubframeCore.Tooltip.Root>
                      <SubframeCore.Tooltip.Trigger asChild={true}>
                        <span className="cursor-pointer mt-0.5">
                          <FeatherInfo className="h-3 w-3 text-subtext-color" />
                        </span>
                      </SubframeCore.Tooltip.Trigger>
                      <SubframeCore.Tooltip.Portal>
                        <SubframeCore.Tooltip.Content
                          side="top"
                          align="start"
                          sideOffset={4}
                          asChild={true}
                        >
                          <Tooltip className="z-[9999]">
                            <div className="flex flex-col gap-0.5">
                              <span>
                                Source:{" "}
                                {(() => {
                                  switch (recommendationSource) {
                                    case "knn":
                                      return "K-NN (similar past transaction)";
                                    case "override":
                                      return "Override rule";
                                    case "direct_match":
                                      return "Direct match (party master)";
                                    case "amount_match":
                                      return "Amount match (open invoice)";
                                    case "ledger_match":
                                      return "Ledger match (chart of accounts)";
                                    case "vendormatching":
                                      return "LLM suggestion";
                                    default:
                                      return recommendationSource;
                                  }
                                })()}
                              </span>
                              {recommendationConfidence != null &&
                                recommendationConfidence !== "" && (
                                  <span>
                                    Confidence:{" "}
                                    {Math.round(
                                      (typeof recommendationConfidence ===
                                      "string"
                                        ? parseFloat(recommendationConfidence)
                                        : recommendationConfidence) * 100,
                                    )}
                                    %
                                  </span>
                                )}
                            </div>
                          </Tooltip>
                        </SubframeCore.Tooltip.Content>
                      </SubframeCore.Tooltip.Portal>
                    </SubframeCore.Tooltip.Root>
                  </SubframeCore.Tooltip.Provider>
                )}
              </div>
            )}
          </div>
          {selectedCustomer && (
            <div className="flex items-center gap-2">
              <TextField
                className="h-8 w-48"
                label=""
                helpText=""
                icon={<FeatherSearch />}
              >
                <TextField.Input
                  placeholder="Search invoices"
                  value={invoiceSearchTerm}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setInvoiceSearchTerm(event.target.value);
                  }}
                />
              </TextField>
            </div>
          )}
          {showRemoveButton && onRemove && (
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={<FeatherX />}
              onClick={onRemove}
            >
              Remove
            </Button>
          )}
        </div>

        {/* Bank Exchange Rate Field - shown only for foreign currency */}
        {hasForeignCurrency() && selectedCustomer && (
          <div className="flex w-full flex-col items-start gap-2 mb-4">
            <span className="text-body-bold font-body-bold text-default-font">
              Bank Exchange Rate
            </span>
            <div className="flex w-full items-center gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-default-font">
                  Exchange Rate:
                </span>
                <TextField
                  className="h-auto w-40 flex-none"
                  label=""
                  helpText=""
                >
                  <TextField.Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="Enter rate"
                    value={bankExchangeRateInput}
                    onFocus={() => setIsBankExchangeRateFocused(true)}
                    onBlur={() => setIsBankExchangeRateFocused(false)}
                    onWheel={handleNumberInputWheel}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const inputValue = event.target.value;
                      setBankExchangeRateInput(inputValue);

                      if (inputValue === "") {
                        onBankExchangeRateChange(null);
                        return;
                      }

                      const decimalRegex = /^\d*\.?\d*$/;
                      if (!decimalRegex.test(inputValue)) {
                        return;
                      }

                      if (inputValue === "." || inputValue.endsWith(".")) {
                        const numericValue =
                          inputValue === "." ? null : parseFloat(inputValue);
                        onBankExchangeRateChange(numericValue);
                        return;
                      }

                      const value = parseFloat(inputValue);
                      if (!isNaN(value)) {
                        onBankExchangeRateChange(value);
                      }
                    }}
                  />
                </TextField>
                <span className="text-body font-body text-default-font text-neutral-500">
                  (Required for foreign currency transactions)
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Available Invoices */}
        {selectedCustomer && (
          <div className="flex w-full flex-col items-start gap-2">
            <div className="flex w-full items-center justify-between">
              <span className="text-body-bold font-body-bold text-default-font">
                Available Invoices
                {loadingInvoices && (
                  <span className="text-body font-body text-neutral-500 ml-2">
                    (Loading...)
                  </span>
                )}
              </span>
              <div className="flex items-center gap-4">
                {!hasForeignCurrency() &&
                  selectedInvoices.some((item) => item.isSelected) && (
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      onClick={handlePayInFull}
                      disabled={payInFullUsed}
                    >
                      Pay in Full
                    </Button>
                  )}
              </div>
            </div>
            {selectedInvoices.length > 0 ? (
              selectedInvoices.map((item) => (
                <div
                  key={item.invoice.invoice_id}
                  className="flex w-full flex-col gap-2"
                >
                  <div
                    className={`flex w-full items-center rounded-md border border-solid px-4 py-3 ${
                      item.isSelected
                        ? item.amount > 0
                          ? "border-success-600 bg-success-100"
                          : "border-warning-600 bg-warning-50"
                        : "border-neutral-border bg-default-background"
                    }`}
                  >
                    {/* Column 1: Select button + Invoice number + Date - Fixed width */}
                    <div className="flex items-center gap-4 w-56 flex-shrink-0">
                      <Button
                        className={`h-6 w-6 flex-none ${
                          item.isSelected
                            ? "bg-success-600 text-white"
                            : "border-2 border-dashed border-neutral-300"
                        }`}
                        variant={
                          item.isSelected
                            ? "brand-primary"
                            : "neutral-secondary"
                        }
                        size="small"
                        icon={item.isSelected ? <FeatherCheck /> : undefined}
                        onClick={() =>
                          handleInvoiceSelect(item.invoice.invoice_id)
                        }
                      />
                      <div className="flex flex-col items-start min-w-0">
                        <span className="text-body-bold font-body-bold text-default-font truncate w-full">
                          {item.invoice.invoice_number}
                        </span>
                        <span className="text-body font-body text-neutral-500">
                          {new Date(item.invoice.date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Column 2: Full/Paid/Remaining amounts - Fixed width */}
                    <div className="flex items-center gap-8 w-72 flex-shrink-0 justify-center">
                      <div className="flex flex-col items-center w-20">
                        <span className="text-caption font-caption text-neutral-400">
                          Full
                        </span>
                        <span className="text-body font-body text-default-font">
                          {item.invoice.currency_symbol}
                          {formatAmount(item.invoice.total)}
                        </span>
                      </div>
                      <div className="flex flex-col items-center w-20">
                        <span className="text-caption font-caption text-neutral-400">
                          Paid
                        </span>
                        <span className="text-body font-body text-default-font">
                          {item.invoice.currency_symbol}
                          {formatAmount(
                            item.invoice.total - item.invoice.balance,
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col items-center w-24">
                        <span className="text-caption font-caption text-neutral-400">
                          Remaining
                        </span>
                        <span className="text-body font-body text-default-font">
                          {item.invoice.currency_symbol}
                          {formatAmount(item.invoice.balance)}
                        </span>
                      </div>
                    </div>

                    {/* Column 3: Amount input + TDS dropdown - Fixed width */}
                    <div className="flex items-start gap-4 flex-1 justify-end">
                      <div className="flex flex-col items-center gap-1">
                        <TextField
                          className="h-auto w-32 flex-none"
                          label=""
                          helpText=""
                        >
                          <TextField.Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder={`Max: ${
                              item.invoice.currency_symbol
                            }${formatAmount(
                              getMaxAmountForInvoice(item.invoice.invoice_id),
                            )}`}
                            value={
                              item.isSelected
                                ? invoiceAmountInputs[
                                    item.invoice.invoice_id
                                  ] !== undefined
                                  ? invoiceAmountInputs[item.invoice.invoice_id]
                                  : item.amount > 0
                                    ? item.amount.toString()
                                    : ""
                                : ""
                            }
                            disabled={
                              !item.isSelected ||
                              (isInvoiceForeignCurrency(item.invoice) &&
                                (!bankExchangeRate || bankExchangeRate <= 0))
                            }
                            onWheel={handleNumberInputWheel}
                            onFocus={(e) => {
                              focusedInvoiceInputsRef.current.add(
                                item.invoice.invoice_id,
                              );
                            }}
                            onBlur={(e) => {
                              focusedInvoiceInputsRef.current.delete(
                                item.invoice.invoice_id,
                              );
                            }}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              const inputValue = event.target.value;
                              const invoiceId = item.invoice.invoice_id;

                              const updateInputValue = (value: string) => {
                                setInvoiceAmountInputs((prev) => ({
                                  ...prev,
                                  [invoiceId]: value,
                                }));
                              };

                              if (inputValue === "") {
                                updateInputValue("");
                                handleAmountChange(invoiceId, 0);
                                return;
                              }

                              const decimalRegex = /^\d*\.?\d{0,2}$/;
                              if (!decimalRegex.test(inputValue)) {
                                return;
                              }

                              // Real-time validation: Check if the input value would exceed max amount
                              // Only validate if it's a complete number (not ending with decimal point)
                              if (
                                inputValue !== "." &&
                                !inputValue.endsWith(".")
                              ) {
                                const numericValue = parseFloat(inputValue);
                                if (!isNaN(numericValue)) {
                                  const maxAllowed =
                                    getMaxAmountForInvoice(invoiceId);
                                  const roundedMaxAllowed =
                                    Math.round(maxAllowed * 100) / 100;

                                  // If the numeric value exceeds max, prevent the input and show warning
                                  if (
                                    numericValue >
                                    roundedMaxAllowed + 0.001
                                  ) {
                                    const currencySymbol =
                                      item.invoice.currency_symbol || "₹";
                                    const isTdsApplied =
                                      item.invoice.is_tds_applied === 1;
                                    const tdsInfo =
                                      !isTdsApplied && item.tds_tax_amount
                                        ? ` (TDS of ${currencySymbol}${formatAmount(
                                            item.tds_tax_amount,
                                          )} will be deducted separately)`
                                        : "";
                                    showWarning(
                                      `Amount cannot exceed ${currencySymbol}${formatAmount(
                                        roundedMaxAllowed,
                                      )}${tdsInfo}.`,
                                    );
                                    // Don't update the input - keep the previous valid value
                                    return;
                                  }
                                }
                              }

                              if (
                                inputValue === "." ||
                                inputValue.endsWith(".")
                              ) {
                                const numericValue =
                                  inputValue === "."
                                    ? 0
                                    : parseFloat(inputValue) || 0;
                                updateInputValue(inputValue);
                                handleAmountChange(invoiceId, numericValue);
                                return;
                              }

                              const value = parseFloat(inputValue) || 0;
                              const roundedValue =
                                Math.round(value * 100) / 100;
                              updateInputValue(inputValue);
                              handleAmountChange(invoiceId, roundedValue);
                            }}
                          />
                        </TextField>
                        {item.isSelected &&
                          isInvoiceForeignCurrency(item.invoice) &&
                          bankExchangeRate &&
                          bankExchangeRate > 0 &&
                          item.amount > 0 && (
                            <span className="text-xs text-neutral-500">
                              = ₹{formatAmount(item.amount * bankExchangeRate)}
                            </span>
                          )}
                        {shouldShowInvoiceAdjustment(item) && (
                          <div className="flex flex-col items-center gap-1 mt-1">
                            <TextField
                              className="h-auto w-32 flex-none"
                              label=""
                              helpText=""
                            >
                              <TextField.Input
                                className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Adjustment"
                                value={
                                  invoiceAdjustmentInputs[
                                    item.invoice.invoice_id
                                  ] ?? ""
                                }
                                disabled={!item.isSelected}
                                onWheel={handleNumberInputWheel}
                                onChange={(
                                  event: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  const inputValue = event.target.value;
                                  const invoiceId = item.invoice.invoice_id;

                                  if (inputValue === "") {
                                    setInvoiceAdjustmentInputs((prev) => ({
                                      ...prev,
                                      [invoiceId]: "",
                                    }));
                                    handleAdjustmentChange(invoiceId, 0);
                                    return;
                                  }

                                  const decimalRegex = /^\d*\.?\d{0,2}$/;
                                  if (!decimalRegex.test(inputValue)) {
                                    return;
                                  }

                                  if (
                                    inputValue !== "." &&
                                    !inputValue.endsWith(".")
                                  ) {
                                    const numericValue = parseFloat(inputValue);
                                    if (!isNaN(numericValue)) {
                                      const maxAllowed =
                                        getInvoiceAdjustmentLimit(item);
                                      if (numericValue > maxAllowed + 0.001) {
                                        showWarning(
                                          `Adjustment cannot exceed ${
                                            item.invoice.currency_symbol || "₹"
                                          }${formatAmount(maxAllowed)}.`,
                                        );
                                        return;
                                      }
                                    }
                                  }

                                  setInvoiceAdjustmentInputs((prev) => ({
                                    ...prev,
                                    [invoiceId]: inputValue,
                                  }));
                                  handleAdjustmentChange(
                                    invoiceId,
                                    parseFloat(inputValue) || 0,
                                  );
                                }}
                              />
                            </TextField>
                            <span className="text-caption font-caption text-neutral-500 text-center leading-tight">
                              Rounding adjustment
                            </span>
                          </div>
                        )}
                      </div>
                      {!isInvoiceForeignCurrency(item.invoice) && (
                        <div className="flex flex-col items-center gap-1">
                          {item.is_tds_applied === 1 ? (
                            <div className="h-auto w-32 flex-none flex items-center justify-center px-1.5 py-1.5 bg-neutral-100 rounded border border-neutral-300">
                              <span className="text-caption font-caption text-neutral-600 text-center">
                                TDS already applied
                              </span>
                            </div>
                          ) : (
                            <div className="h-auto w-32 flex-none flex items-center justify-center px-1.5 py-1.5 bg-neutral-50 rounded border border-neutral-300">
                              <span className="text-caption font-caption text-neutral-600 text-center">
                                Line-wise TDS
                              </span>
                            </div>
                          )}
                          {item.is_tds_applied === 1 ? (
                            <span className="text-xs text-neutral-500 text-center w-32 truncate">
                              {item.tds_tax_percentage}% - {item.tds_tax_name}
                            </span>
                          ) : (
                            <span className="text-caption font-caption text-neutral-500 text-center w-32">
                              {item.tds_tax_amount
                                ? `${item.invoice.currency_symbol}${formatAmount(
                                    item.tds_tax_amount,
                                  )}`
                                : `${item.invoice.currency_symbol}0.00`}
                            </span>
                          )}
                        </div>
                      )}
                      <Button
                        className={`h-8 w-20 flex-none ${
                          item.isSelected ? "bg-success-600 text-white" : ""
                        }`}
                        size="small"
                        onClick={() =>
                          handleInvoiceSelect(item.invoice.invoice_id)
                        }
                      >
                        {item.isSelected ? "Selected" : "Select"}
                      </Button>
                    </div>
                  </div>
                  {item.isSelected &&
                    !isInvoiceForeignCurrency(item.invoice) && (
                      <div className="ml-10 flex w-[calc(100%-2.5rem)] flex-col gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                        <div className="grid grid-cols-[minmax(180px,1.5fr)_90px_90px_170px_90px] items-center gap-3">
                          <span className="text-caption-bold font-caption-bold text-subtext-color">
                            Line item
                          </span>
                          <span className="text-caption-bold font-caption-bold text-subtext-color text-right">
                            Taxable
                          </span>
                          <span className="text-caption-bold font-caption-bold text-subtext-color text-right">
                            GST
                          </span>
                          <span className="text-caption-bold font-caption-bold text-subtext-color">
                            TDS
                          </span>
                          <span className="text-caption-bold font-caption-bold text-subtext-color text-right">
                            TDS Amt
                          </span>
                        </div>
                        {item.lineItemsLoading ? (
                          <div className="flex items-center gap-2 py-2 text-body font-body text-neutral-500">
                            <FeatherLoader className="h-4 w-4 animate-spin" />
                            Loading line items...
                          </div>
                        ) : item.tds_line_items &&
                          item.tds_line_items.length > 0 ? (
                          (() => {
                            const isInvoiceTdsLocked =
                              item.is_tds_applied === 1;
                            return item.tds_line_items.map((line) => (
                              <div
                                key={line.line_item_id}
                                className="grid grid-cols-[minmax(180px,1.5fr)_90px_90px_170px_90px] items-center gap-3 border-t border-solid border-neutral-border pt-2"
                              >
                                <div className="flex min-w-0 flex-col">
                                  <span className="truncate text-body-bold font-body-bold text-default-font">
                                    {line.name}
                                  </span>
                                  {line.description && (
                                    <span className="truncate text-caption font-caption text-neutral-500">
                                      {line.description}
                                    </span>
                                  )}
                                </div>
                                <span className="text-body font-body text-default-font text-right">
                                  {item.invoice.currency_symbol}
                                  {formatAmount(line.item_total)}
                                </span>
                                <span className="text-body font-body text-default-font text-right">
                                  {line.tax_percentage
                                    ? `${line.tax_percentage}%`
                                    : "-"}
                                </span>
                                {isInvoiceTdsLocked ? (
                                  <SubframeCore.Tooltip.Provider>
                                    <SubframeCore.Tooltip.Root>
                                      <SubframeCore.Tooltip.Trigger
                                        asChild={true}
                                      >
                                        <div className="h-auto w-full min-w-0">
                                          <Button
                                            className="h-8 w-full justify-between overflow-hidden cursor-not-allowed"
                                            variant="neutral-secondary"
                                            iconRight={<FeatherChevronDown />}
                                            disabled={true}
                                          >
                                            <span className="truncate text-left text-sm">
                                              {line.tds_tax_id &&
                                              line.tds_tax_name
                                                ? truncateText(
                                                    `${line.tds_tax_percentage}% - ${line.tds_tax_name}`,
                                                    18,
                                                  )
                                                : ""}
                                            </span>
                                          </Button>
                                        </div>
                                      </SubframeCore.Tooltip.Trigger>
                                      <SubframeCore.Tooltip.Portal>
                                        <SubframeCore.Tooltip.Content
                                          side="top"
                                          align="start"
                                          sideOffset={4}
                                          asChild={true}
                                        >
                                          <Tooltip>
                                            TDS has already been recorded for
                                            this invoice. Uncategorise the
                                            previous reconciliation to change
                                            it.
                                          </Tooltip>
                                        </SubframeCore.Tooltip.Content>
                                      </SubframeCore.Tooltip.Portal>
                                    </SubframeCore.Tooltip.Root>
                                  </SubframeCore.Tooltip.Provider>
                                ) : (
                                  <SubframeCore.DropdownMenu.Root>
                                    <SubframeCore.DropdownMenu.Trigger
                                      asChild={true}
                                    >
                                      <div className="h-auto w-full min-w-0">
                                        <Button
                                          className="h-8 w-full justify-between overflow-hidden"
                                          variant="neutral-secondary"
                                          iconRight={<FeatherChevronDown />}
                                        >
                                          <span className="truncate text-left text-sm">
                                            {line.tds_tax_name
                                              ? truncateText(
                                                  `${line.tds_tax_percentage}% - ${line.tds_tax_name}`,
                                                  18,
                                                )
                                              : "No TDS"}
                                          </span>
                                        </Button>
                                      </div>
                                    </SubframeCore.DropdownMenu.Trigger>
                                    <SubframeCore.DropdownMenu.Portal>
                                      <SubframeCore.DropdownMenu.Content
                                        side="bottom"
                                        align="start"
                                        sideOffset={4}
                                        asChild={true}
                                      >
                                        <div className="max-h-48 overflow-y-auto w-72 dropdown-menu-content">
                                          <DropdownMenu>
                                            <DropdownMenu.DropdownItem
                                              onClick={() =>
                                                handleLineTdsSelect(
                                                  item.invoice.invoice_id,
                                                  line.line_item_id,
                                                  null,
                                                )
                                              }
                                            >
                                              No TDS
                                            </DropdownMenu.DropdownItem>
                                            {filterTdsRatesForDocumentDate(
                                              tdsRates.map((rate) => ({
                                                ...rate,
                                                is_expired:
                                                  rate.is_expired ?? true,
                                              })),
                                              item.invoice.date,
                                            ).map((rate) => {
                                              const displayText = `${rate.tds_tax_percentage}% - ${rate.tds_tax_name}`;
                                              return (
                                                <DropdownMenu.DropdownItem
                                                  key={rate.tds_tax_id}
                                                  onClick={() =>
                                                    handleLineTdsSelect(
                                                      item.invoice.invoice_id,
                                                      line.line_item_id,
                                                      rate,
                                                    )
                                                  }
                                                >
                                                  {truncateText(
                                                    displayText,
                                                    48,
                                                  )}
                                                </DropdownMenu.DropdownItem>
                                              );
                                            })}
                                          </DropdownMenu>
                                        </div>
                                      </SubframeCore.DropdownMenu.Content>
                                    </SubframeCore.DropdownMenu.Portal>
                                  </SubframeCore.DropdownMenu.Root>
                                )}
                                <span className="text-body font-body text-default-font text-right">
                                  {item.invoice.currency_symbol}
                                  {formatAmount(line.tds_tax_amount || 0)}
                                </span>
                              </div>
                            ));
                          })()
                        ) : (
                          <span className="border-t border-solid border-neutral-border pt-2 text-body font-body text-neutral-500">
                            No line items available for this invoice.
                          </span>
                        )}
                      </div>
                    )}
                </div>
              ))
            ) : !loadingInvoices ? (
              <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-6">
                <span className="text-body font-body text-neutral-500">
                  No invoices found for {selectedCustomer.customer_name}
                </span>
              </div>
            ) : null}
            {invoicesHasMore && (
              <div className="flex w-full items-center justify-center pt-2">
                <Button
                  variant="neutral-secondary"
                  size="small"
                  onClick={loadMoreInvoices}
                  disabled={invoicesLoading}
                  loading={invoicesLoading}
                >
                  {invoicesLoading ? "Loading..." : "Load More Invoices"}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Forex Calculations */}
        {hasForeignCurrency() &&
          bankExchangeRate &&
          bankExchangeRate > 0 &&
          selectedInvoices.some((item) => item.isSelected && item.amount > 0) &&
          selectedCustomer && (
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Forex Calculations
              </span>
              <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Forex Gain/Loss
                  </span>
                  <span
                    className={`text-body-bold font-body-bold ${
                      forexDetails.forexGainOrLoss >= 0
                        ? "text-success-600"
                        : "text-error-600"
                    }`}
                  >
                    ₹{formatAmount(forexDetails.forexGainOrLoss)}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Bank Charges
                  </span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    ₹{formatAmount(forexDetails.bankCharges)}
                  </span>
                </div>
              </div>
            </div>
          )}

        {/* Advance Payment */}
        {(knockOffOpeningBalance ||
          isMultipleInstances ||
          (!hasForeignCurrency() && selectedCustomer)) && (
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              {knockOffOpeningBalance ? "Opening balance" : "Advance Payment"}
            </span>
            <div className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
              <div className="flex items-center gap-4">
                <span className="text-body font-body text-default-font">
                  {knockOffOpeningBalance
                    ? "Opening balance"
                    : "Advance Payment Amount"}
                </span>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-4">
                  <TextField
                    className="h-auto w-40 flex-none"
                    label=""
                    helpText=""
                  >
                    <TextField.Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={advancePaymentInput}
                      disabled={
                        !isMultipleInstances ||
                        (!knockOffOpeningBalance && !canAdvance)
                      }
                      onFocus={() => setIsAdvancePaymentFocused(true)}
                      onBlur={() => setIsAdvancePaymentFocused(false)}
                      onWheel={handleNumberInputWheel}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        if (!isMultipleInstances) return;
                        const inputValue = event.target.value;
                        setAdvancePaymentInput(inputValue);

                        if (inputValue === "") {
                          setAdvancePayment(0);
                          return;
                        }

                        const decimalRegex = /^\d*\.?\d{0,2}$/;
                        if (!decimalRegex.test(inputValue)) {
                          return;
                        }

                        const value = parseFloat(inputValue) || 0;
                        const roundedValue = Math.round(value * 100) / 100;
                        setAdvancePayment(roundedValue);
                      }}
                    />
                  </TextField>
                  <span className="text-body font-body text-default-font w-8">
                    ₹
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

CustomerPaymentReco.displayName = "CustomerPaymentReco";

export default CustomerPaymentReco;
